import { useQuery } from "@tanstack/react-query";
import { shouldShowQueryLoading } from "../../utils/queryHydration";
import Page404 from "../../components/Page404/Page404.tsx";
import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import "../../components/Esg/Esg.scss";
import {
  fetchGlobalData,
  getBlobAssetUrl,
  getStrapiMedia,
  sendStrapiRequest,
} from "../../utils/utils.tsx";
import { Esg_Plain } from "../../types/api/esg-international.ts";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import FooterFormESG from "../../components/FooterFormESG/FooterFormESG.tsx";

const EsgTest = ({ locale }: { locale: string }) => {
  //const params = useParams();
  const numberRefs = useRef<(HTMLDivElement | null)[]>([]);
  const numberObserverRef = useRef<IntersectionObserver | null>(null);

  // Determine the correct API endpoint based on locale
  // For English (empty locale), use 'en' explicitly
  const effectiveLocale = locale || "en";
  const apiEndpoint = `/api/esg-international?customPopulate=nested&locale=${effectiveLocale}`;

  const {
    data: esg,
    error,
    isLoading,
    isError,
  } = useQuery<Esg_Plain>({
    queryKey: [apiEndpoint],
    queryFn: ({ queryKey }) => fetchGlobalData(queryKey[0] as string),
  });

  // Состояние для отслеживания открытых элементов в каждом списке аккордеона
  const [openedItems, setOpenedItems] = useState<{
    [key: string]: number | null;
  }>({
    "esg-accordion": null,
    "esg-faq__section-environmental": null,
    "esg-faq__section-social": null,
    "esg-faq__section-governance": null,
  });

  // Discount form state
  const [discountFormState, setDiscountFormState] = useState<{
    [blockIndex: number]: {
      email: string;
      loading: boolean;
      error: string | null;
      success: boolean;
    };
  }>({});

  const getDiscountFormState = (blockIndex: number) => {
    return (
      discountFormState[blockIndex] || {
        email: "",
        loading: false,
        error: null,
        success: false,
      }
    );
  };

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleDiscountFormChange = (blockIndex: number, email: string) => {
    setDiscountFormState((prev) => ({
      ...prev,
      [blockIndex]: { ...getDiscountFormState(blockIndex), email, error: null },
    }));
  };

  const handleDiscountFormSubmit = async (
    e: React.FormEvent,
    blockIndex: number,
    formTo: string,
    formName: string,
  ) => {
    e.preventDefault();
    const state = getDiscountFormState(blockIndex);

    if (!state.email) {
      setDiscountFormState((prev) => ({
        ...prev,
        [blockIndex]: { ...state, error: "Please enter your email" },
      }));
      return;
    }

    if (!validateEmail(state.email)) {
      setDiscountFormState((prev) => ({
        ...prev,
        [blockIndex]: { ...state, error: "Please enter a valid email" },
      }));
      return;
    }

    setDiscountFormState((prev) => ({
      ...prev,
      [blockIndex]: { ...state, loading: true, error: null },
    }));

    try {
      await sendStrapiRequest("/api/forms", {
        form: formName || "ESG Discount Form",
        to: formTo,
        email: state.email,
        name: "",
        company: "",
        message: "",
      });

      setDiscountFormState((prev) => ({
        ...prev,
        [blockIndex]: { email: "", loading: false, error: null, success: true },
      }));
    } catch {
      setDiscountFormState((prev) => ({
        ...prev,
        [blockIndex]: {
          ...state,
          loading: false,
          error: "Failed to submit. Please try again.",
        },
      }));
    }
  };

  // Обработчик клика по элементу аккордеона
  const handleAccordionClick = (
    sectionId: string,
    index: number,
    hasContent: boolean,
  ) => {
    setOpenedItems((prev) => {
      const currentIndex = prev[sectionId];
      if (currentIndex === index) {
        return { ...prev, [sectionId]: null };
      } else {
        return { ...prev, [sectionId]: hasContent ? index : null };
      }
    });
  };

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.body.classList.add("esg-page-body");
    }
    return () => {
      if (typeof document !== "undefined") {
        document.body.classList.remove("esg-page-body");
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const root = document.querySelector<HTMLElement>(".product-page-features");
    if (!root) return;

    const items = Array.from(
      root.querySelectorAll<HTMLDivElement>(".product-page-features__item"),
    );
    if (!items.length) return;

    // Initialize
    items.forEach((item) => {
      const numEl = item.querySelector<HTMLDivElement>(
        ".product-page-features__num",
      );
      const lineEl = item.querySelector<HTMLDivElement>(
        ".product-page-features__cont-line",
      );
      const title = item.querySelector<HTMLDivElement>(
        ".product-page-features__title",
      );
      const info = item.querySelector<HTMLDivElement>(
        ".product-page-features__info",
      );
      if (numEl) numEl.style.display = "none";
      if (lineEl) {
        lineEl.style.width = "0%";
      }
      if (title) title.style.opacity = "0.3";
      if (info) info.style.opacity = "0.3";
    });

    let ticking = false;
    let hasScrolled = false; // Flag to track if the user has scrolled

    // Detect initial scroll to avoid instant jump
    const handleInitialScroll = () => {
      hasScrolled = true;
      window.removeEventListener("scroll", handleInitialScroll);
    };
    window.addEventListener("scroll", handleInitialScroll, { passive: true });

    const compute = () => {
      ticking = false;
      const vh = window.innerHeight;
      const START = vh * 0.9;
      const END = vh * 0.65;

      // Find the index of the current element
      let currentIndex = -1;
      let currentProgress = 0;

      items.forEach((item, idx) => {
        const r = item.getBoundingClientRect();
        const raw = (START - r.top) / (START - END);
        const progress = Math.max(0, Math.min(1, raw));
        if (progress > 0 && progress < 1 && currentIndex === -1) {
          currentIndex = idx;
          currentProgress = progress;
        }
      });

      if (currentIndex === -1) {
        items.forEach((item, idx) => {
          const r = item.getBoundingClientRect();
          const raw = (START - r.top) / (START - END);
          if (raw >= 1) currentIndex = idx;
        });
        currentProgress = 1; // прогресс завершён
      }

      if (currentIndex === -1 && currentProgress === 0) {
        // If nothing is in progress, find the last fully completed element
        items.forEach((item, idx) => {
          const r = item.getBoundingClientRect();
          const raw = (START - r.top) / (START - END);
          if (raw >= 1) {
            currentIndex = idx;
          }
        });
      }

      items.forEach((item, idx) => {
        const numEl = item.querySelector<HTMLDivElement>(
          ".product-page-features__num",
        );
        const lineEl = item.querySelector<HTMLDivElement>(
          ".product-page-features__cont-line",
        );
        const title = item.querySelector<HTMLDivElement>(
          ".product-page-features__title",
        );
        const info = item.querySelector<HTMLDivElement>(
          ".product-page-features__info",
        );

        const effectiveProgress = hasScrolled ? currentProgress : 0;
        const isLastItemDone =
          idx === items.length - 1 && effectiveProgress >= 1;

        if (idx < currentIndex) {
          // Previous elements
          if (lineEl) lineEl.style.width = "0%";
          if (title) title.style.opacity = "1";
          if (info) info.style.opacity = "1";
          if (numEl) numEl.style.display = "none";
        } else if (idx === currentIndex) {
          // Current element
          if (isLastItemDone) {
            // Последний элемент завершил анимацию
            if (lineEl) lineEl.style.width = "0%"; // скрываем линию
            if (title) title.style.opacity = "1"; // текст полностью видим
            if (info) info.style.opacity = "1";
            if (numEl) numEl.style.display = "block";
          } else {
            // Анимация в процессе
            if (lineEl)
              lineEl.style.width = `${(effectiveProgress * 100).toFixed(3)}%`;
            const o = (0.3 + effectiveProgress * 0.7).toFixed(3);
            if (title) title.style.opacity = o;
            if (info) info.style.opacity = o;
            if (numEl)
              numEl.style.display =
                effectiveProgress > 0 && effectiveProgress < 1
                  ? "block"
                  : "none";
          }
        } else {
          // Future elements
          if (lineEl) lineEl.style.width = "0%";
          if (title) title.style.opacity = "0.3";
          if (info) info.style.opacity = "0.3";
          if (numEl) numEl.style.display = "none";
        }
      });
    };

    const onScroll = () => {
      const contRect = root.getBoundingClientRect();
      const outOfView =
        contRect.bottom < 0 || contRect.top > window.innerHeight * 1.2;
      if (outOfView) return;

      if (!ticking) {
        ticking = true;
        requestAnimationFrame(compute);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scroll", handleInitialScroll);
    };
  }, [esg]);

  useEffect(() => {
    // Small delay to ensure refs are set after render
    const timeoutId = setTimeout(() => {
      const animateNumber = (
        element: HTMLDivElement,
        endValue: number,
        duration: number,
        decimals: number = 0,
      ) => {
        let startTimestamp: number | null = null;
        const startValue = Math.max(0, endValue - (decimals > 0 ? 1 : 15));

        const step = (timestamp: number) => {
          if (!startTimestamp) startTimestamp = timestamp;
          const progress = Math.min((timestamp - startTimestamp) / duration, 1);
          const currentValue = startValue + (endValue - startValue) * progress;
          const unitMatch = element.getAttribute("data-unit") || "";
          element.textContent =
            decimals > 0
              ? currentValue.toFixed(decimals) + unitMatch
              : Math.floor(currentValue).toString() + unitMatch;

          if (progress < 1) {
            requestAnimationFrame(step);
          } else {
            const unitMatch = element.getAttribute("data-unit") || "";
            element.textContent =
              decimals > 0
                ? endValue.toFixed(decimals) + unitMatch
                : endValue.toString() + unitMatch;
          }
        };

        requestAnimationFrame(step);
      };

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const element = entry.target as HTMLDivElement;
              // Unobserve immediately to prevent re-triggering
              observer.unobserve(element);

              const rawValue = element.getAttribute("data-num") || "0";
              const normalizedValue = rawValue.replace(",", ".");
              const endValue = parseFloat(normalizedValue) || 0;
              const decimals = normalizedValue.includes(".")
                ? normalizedValue.split(".")[1]?.length || 0
                : 0;
              const startValue = Math.max(
                0,
                endValue - (decimals > 0 ? 1 : 15),
              );
              const unitMatch = element.getAttribute("data-unit") || "";
              element.textContent =
                decimals > 0
                  ? startValue.toFixed(decimals) + unitMatch
                  : startValue.toString() + unitMatch;
              animateNumber(element, endValue, 2000, decimals);
            }
          });
        },
        { threshold: 0.5 },
      );

      numberRefs.current.forEach((ref) => {
        if (ref) {
          observer.observe(ref);
        }
      });

      // Store observer for cleanup
      numberObserverRef.current = observer;
    }, 100);

    // Store current ref values in local variable for cleanup
    const currentRefs = numberRefs.current;

    return () => {
      clearTimeout(timeoutId);
      if (numberObserverRef.current) {
        currentRefs.forEach((ref) => {
          if (ref) {
            numberObserverRef.current!.unobserve(ref);
          }
        });
      }
    };
  }, [esg]);

  const hoverVideoRef = useRef<HTMLVideoElement | null>(null);
  const hoverAnimFrame = useRef<number | null>(null);

  const playForward = () => {
    if (!hoverVideoRef.current) return;
    if (hoverAnimFrame.current) cancelAnimationFrame(hoverAnimFrame.current);
    hoverVideoRef.current.play();
  };

  const playReverse = () => {
    if (!hoverVideoRef.current) return;
    if (hoverAnimFrame.current) cancelAnimationFrame(hoverAnimFrame.current);
    hoverVideoRef.current.play();
  };

  if (shouldShowQueryLoading(isLoading, esg))
    return <div className="loading-block" data-testid="loading-block"></div>;
  console.log(error);
  if (isError) return <div>Error: {error.message}</div>;
  if (!esg) return <div>Global error: fetch homepage</div>;
  if (!esg.data.Blocks.length) {
    return <Page404 locale={""} />;
  }
  console.log(esg.data);

  return (
    <>
      <div className="esg-page">
        {esg.data.contactTitle ? (
          <div className="btn-esg-contact">
            <a className="btn-border" href={esg.data.contactLink}>
              {esg.data.contactTitle}
            </a>
          </div>
        ) : (
          ""
        )}
        {/* eslint-disable @typescript-eslint/no-explicit-any -- Dynamic CMS component types */}
        {esg.data.Blocks?.map((block: any, index: number) => {
          switch (block.__component) {
            case "esg.start": {
              //console.log("FOUND", block);
              const hasContent =
                block.title || block.description || block.buttons?.length;
              return (
                <section key={index} className={"esg-main " + block.classType}>
                  {hasContent && (
                    <div className="wrapper">
                      <div className="esg-main__cont">
                        {block.title && <h1>{block.title}</h1>}
                        {block.description && <p>{block.description}</p>}
                        {block.buttons?.map((btn: any, index: number) => (
                          <div className="esg-main__btn">
                            <a
                              key={index}
                              className={
                                index === 0 ? "btn-black" : "btn-border"
                              }
                              href={btn.link}
                            >
                              {btn.title}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="esg-main__img">
                    {block.image ? (
                      block.image.mime?.startsWith("video/") ||
                      block.image.url?.endsWith(".mp4") ? (
                        <video
                          autoPlay
                          loop
                          muted
                          playsInline
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        >
                          <source
                            src={getStrapiMedia(block.image.url)}
                            type={block.image.mime || "video/mp4"}
                          />
                        </video>
                      ) : (
                        <img src={getStrapiMedia(block.image.url)} alt="" />
                      )
                    ) : (
                      ""
                    )}
                  </div>
                </section>
              );
            }

            case "esg.accordion":
              //console.log("FOUND", block);
              return (
                <div
                  className={"esg-accordion accordion-inf " + block.classType}
                >
                  <div className="wrapper">
                    <div className="esg-accordion__left">
                      {block.image ? (
                        <img src={getStrapiMedia(block.image.url)} alt="" />
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="esg-accordion__right">
                      <div className="accordion-inf-list">
                        {block.items?.map((item: any, index: number) => (
                          <div
                            key={index}
                            className={`accordion-inf-list__item ${
                              openedItems["esg-accordion"] === index
                                ? "opened"
                                : ""
                            }`}
                            onClick={() =>
                              handleAccordionClick("esg-accordion", index, true)
                            }
                          >
                            <div className="accordion-inf-list__title">
                              {item.title}
                              <div className="show-full">
                                {openedItems["esg-accordion"] === index
                                  ? "Hide"
                                  : "More"}
                              </div>
                            </div>
                            <div className="accordion-inf-list__cont">
                              {item.textFormat ? (
                                <BlocksRenderer content={item.textFormat} />
                              ) : (
                                <p>{item.text}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="esg-accordion__right-inf">
                        <div className="esg-accordion__right-inf-img">
                          {block.image ? (
                            <img src={getStrapiMedia(block.image.url)} alt="" />
                          ) : (
                            ""
                          )}
                        </div>
                        <div className="esg-accordion__right-inf-text">
                          <p>{block.description}</p>
                          <div className="esg-accordion__right-btn">
                            {block.buttonText ? (
                              <a className="btn-black" href={block.buttonLink}>
                                {block.buttonText}
                              </a>
                            ) : (
                              ""
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );

            case "esg.goals":
              //console.log("FOUND", block);
              return (
                <div
                  key={index}
                  className="esg-block-inf"
                  style={{
                    backgroundImage: block.image?.url
                      ? `url(${getStrapiMedia(block.image.url)})`
                      : "none",
                  }}
                >
                  <div
                    className="esg-block-inf__img"
                    style={{
                      backgroundImage: block.image?.url
                        ? `url(${getStrapiMedia(block.image.url)})`
                        : "none",
                    }}
                  >
                    {block.image ? (
                      <img src={getStrapiMedia(block.image.url)} alt="" />
                    ) : (
                      ""
                    )}
                  </div>
                  <div className="wrapper">
                    <div className="esg-block-inf__title">
                      <div className="block-title">{block.title}</div>
                      <h2>{block.heading}</h2>
                      <p>{block.description}</p>
                    </div>
                    <div className="esg-block-inf__text">
                      <div className="esg-block-inf__text-title">
                        {block.goalsTitle ? (
                          <div className="block-title">{block.goalsTitle}</div>
                        ) : (
                          ""
                        )}
                      </div>
                      <div className="esg-block-inf__text-cont">
                        <ol>
                          {block.items?.map((goal: any, goalIndex: number) => (
                            <li key={goalIndex}>
                              {goal.text}
                              {goal.formatText ? (
                                <BlocksRenderer content={goal.formatText} />
                              ) : (
                                ""
                              )}
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>
                    {block.resultText && (
                      <div className="esg-block-inf__text">
                        <div className="esg-block-inf__text-title">
                          <div className="block-title">{block.resultTitle}</div>
                        </div>
                        <div className="esg-block-inf__text-cont">
                          <p>{block.resultText}</p>
                        </div>
                      </div>
                    )}
                    {block.resultAlterTitle ? (
                      <div className="esg-block-inf__text">
                        <div className="esg-block-inf__text-title">
                          {block.resultAlterTitle ? (
                            <div className="block-title">
                              {block.resultAlterTitle}
                            </div>
                          ) : (
                            ""
                          )}
                        </div>
                        <div className="esg-block-inf__text-cont">
                          <ol>
                            {block.resultAlterItems?.map(
                              (goal: any, goalIndex: number) => (
                                <li key={goalIndex}>
                                  {goal.text}
                                  {goal.formatText ? (
                                    <BlocksRenderer content={goal.formatText} />
                                  ) : (
                                    ""
                                  )}
                                </li>
                              ),
                            )}
                          </ol>
                        </div>
                      </div>
                    ) : (
                      ""
                    )}
                    <div
                      className="esg-block-inf__footer"
                      dangerouslySetInnerHTML={{ __html: block.footerText }}
                    />
                  </div>
                </div>
              );

            case "esg.faq-block":
              //console.log("FOUND", block);
              return (
                <div className="esg-faq">
                  <div className="wrapper">
                    <h2>{block.title}</h2>
                    {block.categories?.map(
                      (faqSection: any, faqIndex: number) => (
                        <div
                          key={faqIndex}
                          className="esg-faq__section accordion-inf"
                          id={`esg-faq__section-${faqSection.title.toLowerCase()}`}
                        >
                          <div className="block-title">{faqSection.title}</div>
                          <div className="accordion-inf-list">
                            {faqSection.items?.map(
                              (item: any, itemIndex: number) => (
                                <div
                                  key={itemIndex}
                                  className={`accordion-inf-list__item ${
                                    openedItems[
                                      `esg-faq__section-${faqSection.title.toLowerCase()}`
                                    ] === itemIndex
                                      ? "opened"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    handleAccordionClick(
                                      `esg-faq__section-${faqSection.title.toLowerCase()}`,
                                      itemIndex,
                                      true,
                                    )
                                  }
                                >
                                  <div className="accordion-inf-list__title">
                                    {item.question}
                                    <div className="show-full">
                                      {openedItems[
                                        `esg-faq__section-${faqSection.title.toLowerCase()}`
                                      ] === itemIndex
                                        ? "Hide"
                                        : "More"}
                                    </div>
                                  </div>
                                  <div className="accordion-inf-list__cont">
                                    <BlocksRenderer content={item.answer} />
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      ),
                    )}
                    <div className="esg-faq__footer">
                      <p>{block.footerText}</p>
                      <div className="esg-faq__footer-btn">
                        {block.buttonText ? (
                          <a className="btn-black" href={block.buttonLink}>
                            {block.buttonText}
                          </a>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );

            case "esg.documents":
              //console.log("FOUND", block);
              return (
                <div className="esg-documents">
                  <div className="wrapper">
                    <h2>{block.title}</h2>
                    <div className="document-list">
                      {block.files?.map(
                        (documentItem: any, docIndex: number) => (
                          <div key={docIndex} className="document-list__item">
                            <div className="document-list__name">
                              {documentItem.name}
                            </div>
                            <div className="document-list__bottom">
                              <div className="document-list__bottom-left">
                                {(documentItem.type || documentItem.size) && (
                                  <div className="document-list__file-info">
                                    {documentItem.type && (
                                      <span>{documentItem.type}</span>
                                    )}
                                    {documentItem.type &&
                                      documentItem.size &&
                                      ", "}
                                    {documentItem.size && (
                                      <span>{documentItem.size}</span>
                                    )}
                                  </div>
                                )}
                                {documentItem.updated && (
                                  <div className="document-list__updated">
                                    Updated: <span>{documentItem.updated}</span>
                                  </div>
                                )}
                              </div>
                              <div className="document-list__bottom-right">
                                {documentItem.file ? (
                                  <a
                                    href={getStrapiMedia(documentItem.file.url)}
                                    target="_blank"
                                    className="document-icon-btn"
                                  >
                                    <img
                                      src="/img/view.svg"
                                      width="25"
                                      height="14"
                                      alt="View"
                                    />
                                  </a>
                                ) : (
                                  ""
                                )}
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              );

            case "esg.gallery": {
              const variantClass =
                block.variant === "two-columns"
                  ? "esg-block-gallery_2"
                  : block.variant === "one-column"
                    ? "esg-block-gallery_1"
                    : "";
              const slidesConfig =
                block.variant === "two-columns"
                  ? { 768: { slidesPerView: 2 }, 1024: { slidesPerView: 2 } }
                  : block.variant === "one-column"
                    ? { 768: { slidesPerView: 1 }, 1024: { slidesPerView: 1 } }
                    : { 768: { slidesPerView: 2 }, 1024: { slidesPerView: 3 } };
              return (
                <div
                  key={index}
                  className={`esg-block-gallery ${variantClass}`}
                >
                  <div className="wrapper">
                    {block.blockTitle && (
                      <div className="block-title">{block.blockTitle}</div>
                    )}
                    {block.heading && <h2>{block.heading}</h2>}
                    <Swiper
                      modules={[Navigation, Pagination]}
                      spaceBetween={16}
                      slidesPerView={1}
                      navigation
                      pagination={{ clickable: true, type: "fraction" }}
                      breakpoints={slidesConfig}
                      className="esg-gallery-list"
                    >
                      {block.images?.map((image: any, itemIndex: number) => {
                        const isVideo =
                          image.mime?.startsWith("video/") ||
                          image.url?.endsWith(".mp4");
                        return (
                          <SwiperSlide key={itemIndex}>
                            <div className="esg-gallery-list__item">
                              {isVideo ? (
                                <video autoPlay loop muted playsInline>
                                  <source
                                    src={getStrapiMedia(image.url)}
                                    type={image.mime || "video/mp4"}
                                  />
                                </video>
                              ) : (
                                <img
                                  src={getStrapiMedia(image.url)}
                                  width="448"
                                  height="448"
                                  alt={image.alternativeText || ""}
                                />
                              )}
                            </div>
                          </SwiperSlide>
                        );
                      })}
                    </Swiper>
                  </div>
                </div>
              );
            }

            case "esg.team":
              return (
                <div key={index} className="esg-block-team">
                  <div className="wrapper">
                    {block.blockTitle && (
                      <div className="block-title">{block.blockTitle}</div>
                    )}
                    {block.heading && <h2>{block.heading}</h2>}
                    <div className="esg-team-list">
                      {block.members?.map(
                        (member: any, memberIndex: number) => (
                          <div
                            key={memberIndex}
                            className={`esg-team-list__item ${member.variant === "full-photo" ? "full-photo" : ""}`}
                          >
                            <div className="esg-team-list__img">
                              {member.photo && (
                                <img
                                  src={getStrapiMedia(member.photo.url)}
                                  width="680"
                                  height="680"
                                  alt={member.name || ""}
                                />
                              )}
                            </div>
                            <div className="esg-team-list__text">
                              <h2>{member.name}</h2>
                              {member.position && (
                                <div className="esg-team-list__pos">
                                  {member.position}
                                </div>
                              )}
                              {member.bio && (
                                <div className="esg-team-list__cont">
                                  <BlocksRenderer content={member.bio} />
                                </div>
                              )}
                              {member.socialLink && (
                                <div className="header-social">
                                  <ul>
                                    <li>
                                      <a
                                        href={member.socialLink}
                                        target="_blank"
                                      >
                                        <img
                                          width="50"
                                          alt="social"
                                          src={
                                            member.socialIcon
                                              ? getStrapiMedia(
                                                  member.socialIcon.url,
                                                )
                                              : getBlobAssetUrl(
                                                  "soc_1_4c09f64443.svg",
                                                )
                                          }
                                        />
                                      </a>
                                    </li>
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              );

            case "esg.discount": {
              const hasBgImage = !!block.backgroundImage;
              const discountClasses = [
                "esg-block-discount",
                block.btnFull && "esg-discount_btn-full",
                block.layoutCentered && "esg-discount_valign-center",
                block.layoutCentered && "esg-discount_align-center",
                block.layoutCentered && "esg-discount_height-auto",
                !hasBgImage &&
                  block.backgroundColor === "yellow" &&
                  "esg-discount_yellow",
                !hasBgImage &&
                  block.backgroundColor === "white" &&
                  "esg-discount_white",
                block.titleStyle === "large" && "esg-discount_large-title",
                block.titleStyle === "full-width" &&
                  "esg-discount_full-width-title",
                hasBgImage && "esg-discount_with-bg",
                hasBgImage && "esg-discount_white-text",
              ]
                .filter(Boolean)
                .join(" ");
              const bgStyle = block.backgroundImage
                ? {
                    backgroundImage: `url(${getStrapiMedia(block.backgroundImage.url)})`,
                  }
                : {};
              return (
                <div key={index} className={discountClasses}>
                  <div className="wrapper">
                    <div className="esg-discount" style={bgStyle}>
                      {block.topText && (
                        <div className="esg-discount__top-text">
                          {block.topText}
                        </div>
                      )}
                      {block.title && (
                        <div className="esg-discount__title">{block.title}</div>
                      )}
                      <div className="esg-discount__form">
                        {block.showForm ? (
                          getDiscountFormState(index).success ? (
                            <div className="esg-discount__form-success">
                              Thank you! Your message has been sent.
                            </div>
                          ) : (
                            <form
                              onSubmit={(e) =>
                                handleDiscountFormSubmit(
                                  e,
                                  index,
                                  block.formTo,
                                  block.title,
                                )
                              }
                            >
                              <div className="esg-discount__form-field">
                                <input
                                  type="email"
                                  value={getDiscountFormState(index).email}
                                  onChange={(e) =>
                                    handleDiscountFormChange(
                                      index,
                                      e.target.value,
                                    )
                                  }
                                  placeholder={
                                    block.formPlaceholder || "Enter your email"
                                  }
                                  disabled={getDiscountFormState(index).loading}
                                  style={
                                    getDiscountFormState(index).error
                                      ? { borderColor: "red" }
                                      : {}
                                  }
                                />
                              </div>
                              <div className="esg-discount__form-btn">
                                <button
                                  type="submit"
                                  className="btn-black"
                                  disabled={getDiscountFormState(index).loading}
                                >
                                  {getDiscountFormState(index).loading
                                    ? "Sending..."
                                    : block.formButtonText || "Submit"}
                                </button>
                              </div>
                            </form>
                          )
                        ) : (
                          block.buttonText && (
                            <div className="esg-discount__form-btn">
                              <a
                                className="btn-black"
                                href={block.buttonLink || "#contacts"}
                              >
                                {block.buttonText}
                              </a>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            case "esg.features": {
              const featuresClasses = [
                "product-page-features",
                "esg-block-features",
                block.backgroundColor === "grey" && "esg-block-features_grey",
                block.showInnerNumbers && "esg-block-features_inner-numbers",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <div key={index} className={featuresClasses}>
                  <div className="wrapper">
                    {block.blockTitle && (
                      <div className="block-title">{block.blockTitle}</div>
                    )}
                    {block.heading && <h2>{block.heading}</h2>}
                    <div className="product-page-features__list">
                      {block.innerTitle && (
                        <div className="block-title block-title_inner">
                          {block.innerTitle}
                        </div>
                      )}
                      {block.items?.map((item: any, itemIndex: number) => (
                        <div
                          key={itemIndex}
                          className="product-page-features__item"
                        >
                          <div className="product-page-features__num">
                            <span>
                              {String(itemIndex + 1).padStart(2, "0")}.
                            </span>
                          </div>
                          <div className="product-page-features__cont">
                            <div
                              className="product-page-features__cont-line"
                              style={{ width: 0 }}
                            ></div>
                            <div
                              className="product-page-features__title"
                              style={{ opacity: 0.3 }}
                            >
                              {item.title}
                            </div>
                            <div
                              className="product-page-features__info"
                              style={{ opacity: 0.3 }}
                            >
                              {item.description}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {(block.footerText || block.buttonText) && (
                      <div className="product-page-features__btn">
                        {block.footerText && (
                          <div className="product-page-features__btn-text">
                            {block.footerText}
                          </div>
                        )}
                        {block.buttonText && (
                          <a
                            className="btn-black"
                            href={block.buttonLink || "#contacts"}
                          >
                            {block.buttonText}
                          </a>
                        )}
                      </div>
                    )}
                    {block.decorVideo && (
                      <div className="product-page-features__decor">
                        <video autoPlay loop muted playsInline>
                          <source
                            src={getStrapiMedia(block.decorVideo.url)}
                            type="video/mp4"
                          />
                        </video>
                      </div>
                    )}
                  </div>
                </div>
              );
            }

            case "esg.innumbers":
              return (
                <div key={index} className="esg-block-innumbers">
                  <div className="product-page-innumbers">
                    <div className="wrapper">
                      <div className="product-page-innumbers__left">
                        {block.blockTitle && (
                          <div className="block-title">{block.blockTitle}</div>
                        )}
                      </div>
                      <div className="product-page-innumbers__right">
                        {block.description && (
                          <div className="product-page-innumbers__info">
                            {block.description}
                          </div>
                        )}
                        <div className="product-page-innumbers__list">
                          {block.items?.map((item: any, itemIndex: number) => (
                            <div
                              key={itemIndex}
                              className="product-page-innumbers__feature"
                            >
                              <div
                                className="product-page-innumbers__feature-title"
                                data-num={item.number}
                                data-unit={item.unit || ""}
                                ref={(el) => {
                                  numberRefs.current[itemIndex] = el;
                                }}
                              >
                                0{item.unit || ""}
                              </div>
                              <div className="product-page-innumbers__feature-info">
                                {item.text}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  {(block.image || block.video) && (
                    <div className="product-page-innumbers__img">
                      <div className="wrapper">
                        <div className="product-page-innumbers__img-cont">
                          {block.image && (
                            <img src={getStrapiMedia(block.image.url)} alt="" />
                          )}
                          {block.video && (
                            <video
                              ref={hoverVideoRef}
                              muted
                              playsInline
                              onMouseEnter={playForward}
                              onMouseLeave={playReverse}
                            >
                              <source
                                src={getStrapiMedia(block.video.url)}
                                type="video/mp4"
                              />
                            </video>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );

            case "esg.about-us": {
              const aboutUsClasses = [
                "about-us",
                "about-us-esg",
                block.backgroundColor === "gray" && "about-us_grey-bg",
                block.topMargin === "small" && "about-us_with-small-space",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <section key={index} className={aboutUsClasses}>
                  <div className="wrapper">
                    <div className="about-us__left">
                      {block.blockTitle && (
                        <div className="block-title">{block.blockTitle}</div>
                      )}
                    </div>
                    <div className="about-us__right">
                      {block.column1Text && (
                        <div className="about-us__col">
                          <BlocksRenderer content={block.column1Text} />
                        </div>
                      )}
                      {block.column2Text && (
                        <div className="about-us__col">
                          <BlocksRenderer content={block.column2Text} />
                        </div>
                      )}
                      {block.buttonText && (
                        <div className="about-us__btn">
                          <a
                            className="btn-black"
                            href={block.buttonLink || "#"}
                          >
                            {block.buttonText}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              );
            }

            case "esg.services": {
              const servicesClasses = [
                "product-page-services",
                "product-page-services-esg",
                !block.displaySlider &&
                  "product-page-services-esg_without-slider",
                block.backgroundColor === "gray" &&
                  "product-page-services-esg_grey-bg",
                block.iconSize === "large" &&
                  "product-page-services-esg_large-img",
                block.columnsCount === "4" &&
                  "product-page-services-esg_items-4",
                !block.displaySlider &&
                  block.columnsCount === "4" &&
                  "product-page-services-esg_without-slider-4",
              ]
                .filter(Boolean)
                .join(" ");

              const slidesPerView = block.columnsCount === "4" ? 4 : 3;
              const breakpoints =
                block.columnsCount === "4"
                  ? {
                      0: { slidesPerView: 1 },
                      768: { slidesPerView: 2 },
                      1024: { slidesPerView: 3 },
                      1200: { slidesPerView: 4 },
                    }
                  : {
                      0: { slidesPerView: 1 },
                      768: { slidesPerView: 2 },
                      1024: { slidesPerView: 2 },
                      1200: { slidesPerView: 3 },
                    };

              return (
                <section key={index} className={servicesClasses}>
                  <div className="wrapper">
                    {block.blockTitle && (
                      <div className="block-title">{block.blockTitle}</div>
                    )}
                    {block.heading && <h2>{block.heading}</h2>}
                    <div className="service-list">
                      <div className="service-swiper-container">
                        {block.displaySlider ? (
                          <Swiper
                            modules={[Navigation, Pagination]}
                            spaceBetween={16}
                            slidesPerView={slidesPerView}
                            breakpoints={breakpoints}
                            navigation
                            pagination={{
                              clickable: true,
                              type: "fraction",
                            }}
                            touchRatio={1}
                            className="service-swiper"
                          >
                            {block.items?.map(
                              (item: any, itemIndex: number) => (
                                <SwiperSlide key={itemIndex}>
                                  <div className="service-list__item">
                                    <div className="service-list__text">
                                      <h2>{item.title}</h2>
                                      <p>{item.description}</p>
                                    </div>
                                    <div className="service-list__top">
                                      <div className="service-list__num">
                                        {itemIndex + 1}/{block.items.length}
                                      </div>
                                      <div className="service-list__icon">
                                        {item.icon && (
                                          <img
                                            src={getStrapiMedia(item.icon.url)}
                                            width={
                                              block.iconSize === "large"
                                                ? "152"
                                                : "40"
                                            }
                                            height={
                                              block.iconSize === "large"
                                                ? "152"
                                                : "40"
                                            }
                                            alt=""
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </SwiperSlide>
                              ),
                            )}
                          </Swiper>
                        ) : (
                          block.items?.map((item: any, itemIndex: number) => (
                            <div key={itemIndex} className="service-list__item">
                              <div className="service-list__text">
                                <h2>{item.title}</h2>
                                <p>{item.description}</p>
                              </div>
                              <div className="service-list__top">
                                <div className="service-list__num">
                                  {itemIndex + 1}/{block.items.length}
                                </div>
                                <div className="service-list__icon">
                                  {item.icon && (
                                    <img
                                      src={getStrapiMedia(item.icon.url)}
                                      width={
                                        block.iconSize === "large"
                                          ? "152"
                                          : "40"
                                      }
                                      height={
                                        block.iconSize === "large"
                                          ? "152"
                                          : "40"
                                      }
                                      alt=""
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    {(block.footerText || block.footerButtonText) && (
                      <div className="service-bottom-block">
                        {block.footerText && <p>{block.footerText}</p>}
                        {block.footerButtonText && (
                          <a
                            className="btn-black"
                            href={block.footerButtonLink || "#"}
                          >
                            {block.footerButtonText}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </section>
              );
            }

            case "esg.challenges": {
              const challengesClasses = [
                "box-chellanges",
                block.backgroundColor === "white" && "box-chellanges_white-bg",
                block.backgroundMedia && "box-chellanges_img-bg",
                block.displayType === "numbers" && "box-chellanges_with-num",
              ]
                .filter(Boolean)
                .join(" ");

              const isVideo =
                block.backgroundMedia?.mime?.startsWith("video/") ||
                block.backgroundMedia?.url?.endsWith(".mp4");

              return (
                <section key={index} className={challengesClasses}>
                  {block.backgroundMedia && (
                    <div className="box-chellanges__media">
                      {isVideo ? (
                        <video autoPlay loop muted playsInline>
                          <source
                            src={getStrapiMedia(block.backgroundMedia.url)}
                            type={block.backgroundMedia.mime || "video/mp4"}
                          />
                        </video>
                      ) : (
                        <img
                          src={getStrapiMedia(block.backgroundMedia.url)}
                          alt=""
                        />
                      )}
                    </div>
                  )}
                  <div className="wrapper">
                    {block.blockTitle && (
                      <div className="block-title">{block.blockTitle}</div>
                    )}
                    {block.heading && <h2>{block.heading}</h2>}
                    <div className="chellanges-list">
                      {block.items?.map((item: any, itemIndex: number) => (
                        <div key={itemIndex} className="chellanges-list__item">
                          <div className="chellanges-list__icon">
                            {block.displayType === "numbers" ? (
                              `${String(itemIndex + 1).padStart(2, "0")}.`
                            ) : item.icon ? (
                              <img
                                src={getStrapiMedia(item.icon.url)}
                                width="40"
                                height="40"
                                alt=""
                              />
                            ) : null}
                          </div>
                          <div className="chellanges-list__text">
                            <h2>{item.title}</h2>
                            <p>{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {(block.footerText || block.footerButtonText) && (
                      <div className="service-bottom-block">
                        {block.footerText && <p>{block.footerText}</p>}
                        {block.footerButtonText && (
                          <a
                            className="btn-black"
                            href={block.footerButtonLink || "#"}
                          >
                            {block.footerButtonText}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </section>
              );
            }

            case "esg.partners": {
              if (block.layout === "triple") {
                // Group logos into chunks of 3 for triple layout
                const logoChunks: any[][] = [];
                const logos = block.logos || [];
                for (let i = 0; i < logos.length; i += 3) {
                  logoChunks.push(logos.slice(i, i + 3));
                }

                return (
                  <section key={index} className="box-partenrs">
                    <div className="wrapper">
                      {block.blockTitle && (
                        <div className="block-title">{block.blockTitle}</div>
                      )}
                      {block.heading && <h2>{block.heading}</h2>}
                      <div className="partenrs-list">
                        <Swiper
                          modules={[Navigation, Pagination]}
                          spaceBetween={0}
                          slidesPerView={5}
                          breakpoints={{
                            0: { slidesPerView: 2 },
                            768: { slidesPerView: 3 },
                            1024: { slidesPerView: 4 },
                            1200: { slidesPerView: 5 },
                          }}
                          navigation
                          pagination={{
                            clickable: true,
                            type: "fraction",
                          }}
                          touchRatio={1}
                          className="partner-swiper"
                        >
                          {logoChunks.map((chunk, chunkIndex) => (
                            <SwiperSlide key={chunkIndex}>
                              {chunk.map((logoItem: any, logoIndex: number) => (
                                <div
                                  key={logoIndex}
                                  className="partenrs-list__item"
                                >
                                  {logoItem.logo && (
                                    <img
                                      src={getStrapiMedia(logoItem.logo.url)}
                                      alt=""
                                    />
                                  )}
                                </div>
                              ))}
                            </SwiperSlide>
                          ))}
                        </Swiper>
                      </div>
                    </div>
                  </section>
                );
              } else {
                // Single layout - one logo per slide
                return (
                  <section key={index} className="box-partenrs">
                    <div className="wrapper">
                      {block.blockTitle && (
                        <div className="block-title">{block.blockTitle}</div>
                      )}
                      {block.heading && <h2>{block.heading}</h2>}
                      <div className="partenrs-list">
                        <Swiper
                          modules={[Navigation, Pagination]}
                          spaceBetween={0}
                          slidesPerView={5}
                          breakpoints={{
                            0: { slidesPerView: 2 },
                            768: { slidesPerView: 3 },
                            1024: { slidesPerView: 4 },
                            1200: { slidesPerView: 5 },
                          }}
                          navigation
                          pagination={{
                            clickable: true,
                            type: "fraction",
                          }}
                          touchRatio={1}
                          className="partner-swiper"
                        >
                          {block.logos?.map(
                            (logoItem: any, logoIndex: number) => (
                              <SwiperSlide key={logoIndex}>
                                <div className="partenrs-list__item">
                                  {logoItem.logo && (
                                    <img
                                      src={getStrapiMedia(logoItem.logo.url)}
                                      alt=""
                                    />
                                  )}
                                </div>
                              </SwiperSlide>
                            ),
                          )}
                        </Swiper>
                      </div>
                    </div>
                  </section>
                );
              }
            }

            case "esg.quotes": {
              const isVideo =
                block.backgroundMedia?.mime?.startsWith("video/") ||
                block.backgroundMedia?.url?.endsWith(".mp4");

              return (
                <div key={index} className="product-page-quotes">
                  {block.backgroundMedia && (
                    <div className="product-page-quotes__img">
                      <div className="slider__media">
                        {isVideo ? (
                          <video
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="metadata"
                          >
                            <source
                              src={getStrapiMedia(block.backgroundMedia.url)}
                              type={block.backgroundMedia.mime || "video/mp4"}
                            />
                          </video>
                        ) : (
                          <img
                            src={getStrapiMedia(block.backgroundMedia.url)}
                            alt=""
                          />
                        )}
                      </div>
                    </div>
                  )}
                  <div className="wrapper">
                    <div className="product-page-quotes__list">
                      {block.quotes?.map((quote: any, quoteIndex: number) => (
                        <div
                          key={quoteIndex}
                          className={`product-page-quotes__item ${quote.isLong ? "long" : ""}`}
                        >
                          {quote.text && (
                            <div className="product-page-quotes__text">
                              <BlocksRenderer content={quote.text} />
                            </div>
                          )}
                          {(quote.authorPhoto ||
                            quote.authorName ||
                            quote.authorPosition) && (
                            <div className="product-page-quotes__author">
                              {quote.authorPhoto && (
                                <div className="author__img">
                                  <img
                                    src={getStrapiMedia(quote.authorPhoto.url)}
                                    alt=""
                                  />
                                </div>
                              )}
                              {(quote.authorName || quote.authorPosition) && (
                                <div className="author__inf">
                                  {quote.authorName && (
                                    <div className="author__name">
                                      — {quote.authorName}
                                    </div>
                                  )}
                                  {quote.authorPosition && (
                                    <div className="author__postion">
                                      {quote.authorPosition}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            case "esg.text-block-2": {
              const blockClasses = [
                "text-block-2",
                block.backgroundColor === "grey" && "text-block-2_grey",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <div key={index} className={blockClasses}>
                  <div className="wrapper">
                    {block.blockTitle && (
                      <div className="block-title">{block.blockTitle}</div>
                    )}
                    {block.heading && <h2>{block.heading}</h2>}
                    <div className="product-page-solutions__cont">
                      {block.image && (
                        <div className="product-page-solutions__img">
                          {block.image.mime?.startsWith("video/") ||
                          block.image.url?.endsWith(".mp4") ? (
                            <video autoPlay loop muted playsInline>
                              <source
                                src={getStrapiMedia(block.image.url)}
                                type={block.image.mime || "video/mp4"}
                              />
                            </video>
                          ) : (
                            <img
                              src={getStrapiMedia(block.image.url)}
                              alt={block.image.alternativeText || ""}
                            />
                          )}
                        </div>
                      )}
                      <div className="product-page-solutions__text">
                        {block.text1 && (
                          <div
                            className="product-page-solutions__info1"
                            dangerouslySetInnerHTML={{
                              __html: block.text1 || "",
                            }}
                          />
                        )}
                        {block.text2 && (
                          <div className="product-page-solutions__info2">
                            <BlocksRenderer content={block.text2} />
                          </div>
                        )}
                        {(block.number || block.numberDescription) && (
                          <div className="product-page-solutions-number">
                            <div className="product-page-solutions-number__item">
                              {block.number && (
                                <div className="product-page-solutions-number__count">
                                  {block.number}
                                </div>
                              )}
                              {block.numberDescription && (
                                <div
                                  className="product-page-solutions-number__text"
                                  dangerouslySetInnerHTML={{
                                    __html: block.numberDescription || "",
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        )}
                        {block.buttonText && (
                          <div className="product-page-solutions-btn">
                            <a
                              className="btn-black"
                              href={block.buttonLink || "#contacts"}
                            >
                              {block.buttonText}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            default:
              return null;
          }
        })}
        {/* eslint-enable @typescript-eslint/no-explicit-any */}
      </div>

      {esg.data.contacts ? (
        <FooterFormESG data={esg.data.contacts} locale={""} />
      ) : (
        ""
      )}

      <div className="esg-page-footer">
        <div className="wrapper">
          <a href="/">
            <img
              width="104"
              height="13"
              src={getBlobAssetUrl("D_Trading_logo_2_plashka_aaed9da59d.svg")}
            />
          </a>
        </div>
      </div>
    </>
  );
};

export default EsgTest;
